/**
 * Toestemming vragen voor microfoon en geluid, in EEN handeling.
 *
 * WAAROM DIT OP EEN VASTE PLEK MOET GEBEUREN. Een browser geeft de microfoon
 * pas vrij na een echte klik, en hij staat geluid pas toe na een echte klik.
 * Vraag je dat pas op het moment dat je het nodig hebt, dan verschijnt er
 * midden in een gesprek een dialoogvenster - of erger: er gebeurt niets en de
 * gebruiker denkt dat de app stuk is.
 *
 * Daarom gebeurt allebei op de knop "Ga de kamer in". Dat is een echte klik,
 * en het is het moment waarop de gebruiker verwacht dat er iets gevraagd wordt.
 */

import type { Teksten } from '../teksten';
import { audioSessieType, noteer } from './meetlog';

export type ToegangProbleem =
  | 'geweigerd'
  | 'geen_microfoon'
  | 'in_gebruik'
  | 'geen_https'
  | 'onbekend';

export interface ToegangUitslag {
  microfoon: boolean;
  geluid: boolean;
  probleem: ToegangProbleem | null;
}

/**
 * De melding komt uit de TEKSTEN van de gekozen taal, niet uit dit bestand.
 * Hier staan alleen de codes; wie de app in het Duits leest, hoort ook een
 * Duitse foutmelding te krijgen.
 */
export function toegangMelding(probleem: ToegangProbleem, t: Teksten): string {
  switch (probleem) {
    case 'geweigerd':
      return t.foutMicroGeweigerd;
    case 'geen_microfoon':
      return t.foutGeenMicro;
    case 'in_gebruik':
      return t.foutMicroInGebruik;
    case 'geen_https':
      return t.foutGeenHttps;
    default:
      return t.foutMicroOnbekend;
  }
}

/**
 * Vraagt de microfoon aan en laat hem meteen weer los.
 *
 * Het loslaten is belangrijk: we willen alleen de TOESTEMMING, nu nog geen
 * opname. Blijft het spoor open, dan brandt het opnamelampje de hele tijd en
 * dat is onrustig.
 */
export async function vraagToegang(ontgrendelGeluid: () => void): Promise<ToegangUitslag> {
  // Geluid eerst: dit loopt synchroon binnen de klik en telt daardoor
  // gegarandeerd als gebruikersactie.
  ontgrendelGeluid();

  if (!window.isSecureContext) {
    return { microfoon: false, geluid: true, probleem: 'geen_https' };
  }
  if (navigator.mediaDevices?.getUserMedia === undefined) {
    return { microfoon: false, geluid: true, probleem: 'geen_microfoon' };
  }

  try {
    noteer(`toegang: microfoon vragen (sessie=${audioSessieType()})`);
    const stroom = await navigator.mediaDevices.getUserMedia({ audio: true });
    noteer(`toegang: microfoon toegestaan (sessie=${audioSessieType()})`);
    stroom.getTracks().forEach((spoor) => spoor.stop());
    noteer(`toegang: sporen gestopt (sessie=${audioSessieType()})`);
    return { microfoon: true, geluid: true, probleem: null };
  } catch (error) {
    const naam = error instanceof Error ? error.name : '';
    const probleem: ToegangProbleem =
      naam === 'NotAllowedError'
        ? 'geweigerd'
        : naam === 'NotFoundError'
          ? 'geen_microfoon'
          : naam === 'NotReadableError'
            ? 'in_gebruik'
            : naam === 'SecurityError'
              ? 'geen_https'
              : 'onbekend';
    return { microfoon: false, geluid: true, probleem };
  }
}
